import Vue from 'vue';
import Vuex from 'vuex';
import Api from '@/providers/Api.js';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import { saveToStorage } from '@/helpers/localStorageHelper';
import {
  ROLE_GM_ADMIN,
  ROLE_MSS_ADMIN,
  ROLE_MSS_RECRUITER,
  ROLE_MSS_USER,
  ROLE_NON_MSS_USER,
} from '@/helpers/rbacHelper';
import Utils from '@/providers/Utils';
import jobProfiles, { CLEAR_JOB_PROFILES_MUTATION } from '@/store/jobProfiles';
import boardCertifications from '@/store/boardCertications';
import certifyingBoards from '@/store/certifyingBoards';
import societyCertifyingBoards from '@/store/societyCertifyingBoards';
import membershipTypes from '@/store/membershipTypes';
import memberTypeMappings from '@/store/memberTypeMappings';
import studyTypes from '@/store/studyTypes';
import conditionVariables from '@/store/conditionVariables';
import { logUserEvent } from '@/helpers/LoggerHelper';

Vue.use(Vuex);

//mutations
export const APP_LINK = 'https://phairify.com/';
export const SET_USER_MUTATION = 'SET_USER_MUTATION';
export const SET_USER_PROFILE_MUTATION = 'SET_USER_PROFILE_MUTATION';
export const SET_TOKEN_MUTATION = 'SET_TOKEN_MUTATION';
export const SET_SETTINGS_MUTATION = 'SET_SETTINGS_MUTATION';
export const UPDATE_SETTING_MUTATION = 'UPDATE_SETTING_MUTATION';
export const SET_SOCIETY_MUTATION = 'SET_SOCIETY_MUTATION';
export const ADD_SOCIETY_MUTATION = 'ADD_SOCIETY_MUTATION';
export const UPDATE_SOCIETY_MUTATION = 'UPDATE_SOCIETY_MUTATION';
export const SET_USER_QUESTIONNAIRE_MUTATION =
  'SET_USER_QUESTIONNAIRE_MUTATION';
export const SET_USER_SURVEY_MUTATION = 'SET_USER_SURVEY_MUTATION';
export const SET_PRIMARY_QUESTIONNAIRE_COMPARISONS_MUTATION =
  'SET_PRIMARY_QUESTIONNAIRE_COMPARISONS_MUTATION';
export const UPDATE_SOCIETY_DATA_MUTATION = 'UPDATE_SOCIETY_DATA_MUTATION';
export const UPDATE_USER_SETTING_MUTATION = 'UPDATE_USER_SETTING_MUTATION';
export const SET_USER_SETTINGS_MUTATION = 'SET_USER_SETTINGS_MUTATION';
export const SET_POPUP_VISIBILITY_MUTATION =
  'SET_POPUP_VISIBILITY_FALSE_MUTATION';
export const ADD_SURVEY_DATA_MUTATION = 'ADD_SURVEY_MUTATION';
export const ADD_SPECIALTY_MUTATION = 'ADD_SPECIALTY_MUTATION';
export const UPDATE_SPECIALTY_MUTATION = 'UPDATE_SPECIALTY_MUTATION';
export const DELETE_SPECIALTY_MUTATION = 'DELETE_SPECIALTY_MUTATION';
export const ADD_SUBSPECIALTY_MUTATION = 'ADD_SUBSPECIALTY_MUTATION';
export const UPDATE_SUBSPECIALTY_MUTATION = 'UPDATE_SUBSPECIALTY_MUTATION';
export const DELETE_SUBSPECIALTY_MUTATION = 'DELETE_SUBSPECIALTY_MUTATION';
export const SET_USER_DEFAULT_JOB_PROFILE_MUTATION =
  'SET_USER_DEFAULT_JOB_PROFILE_MUTATION';
export const SET_SURVEYS_LOADING_MUTATION = 'SET_SURVEYS_LOADING_MUTATION';
export const SET_PASSWORD_RESET_REQUIRED_MUTATION =
  'SET_PASSWORD_RESET_REQUIRED_MUTATION';
export const SET_PRESENTATIONAL_QUESTIONNAIRE_MUTATION =
  'SET_PRESENTATIONAL_QUESTIONNAIRE_MUTATION';
export const DELETE_RESET_SURVEY_FROM_COMPLETED_MUTATION =
  'DELETE_RESET_SURVEY_FROM_COMPLETED_MUTATION';
export const SET_USER_QUESTIONNAIRE_LOADING_MUTATION =
  'SET_USER_QUESTIONNAIRE_LOADING_MUTATION';
export const SET_SOCIETIES_OPTIONS_MUTATION = 'SET_SOCIETIES_OPTIONS_MUTATION';
export const SET_GUEST_USER_ROLE_MUTATION = 'SET_GUEST_USER_ROLE_MUTATION';
export const SET_RECRUITER_INFORM_COMPARISONS_MUTATION =
  'SET_RECRUITER_INFORM_COMPARISONS_MUTATION';

//actions
export const GET_CURRENT_USER_ACTION = 'GET_CURRENT_USER_ACTION';
export const GET_SOCIETIES_ACTION = 'GET_SOCIETIES_ACTION';
export const GET_SPECIALTIES_ACTION = 'GET_SPECIALTIES_ACTION';
export const GET_SUBSPECIALTIES_ACTION = 'GET_SUBSPECIALTIES_ACTION';
export const SIGN_OUT_ACTION = 'SIGN_OUT_ACTION';
export const AUTH_ACTION = 'AUTH_ACTION';
export const GET_SETTINGS_ACTION = 'GET_SETTINGS_ACTION';
export const GET_SOCIETY_ACTION = 'GET_SOCIETY_ACTION';
export const ADD_SOCIETY_ACTION = 'ADD_SOCIETY_ACTION';
export const GET_SURVEY_ACTION = 'GET_SURVEY_ACTION';
export const CREATE_SURVEY_ACTION = 'CREATE_SURVEY_ACTION';
export const GET_USER_QUESTIONNAIRE_ACTION = 'GET_USER_QUESTIONNAIRE_ACTION';
export const SWITCH_GUEST_USER_ROLE_ACTION = 'SWITCH_GUEST_USER_ROLE_ACTION';

export const SETTINGS_KEYS = {
  FILE_STORE_URL: 'file_store_url',
  ASSETS_STORE_URL: 'assets_store_url',
  REPORTS_PLACEHOLDER_NAME: 'reports_placeholder_image_name',
  USER_PROFILE_FIELDS_REQUIRED_FOR_JOB_PROFILE:
    'user_profile_fields_required_for_job_profile',
  S3_LOGO_FOLDER_NAME: 's3_logo_folder_name',
  CHARACTER_FOR_JOB_QUESTION_LABEL: 'character_for_job_question_label',
  DEFAULT_SET_ZERO_LABEL: 'default_set_zero_label',
  NON_SOCIETY_ID: 'non_society_id',
};

export const USER_SETTINGS_KEYS = {
  NOT_SHOW_INFO_RESULTS: 'doNotShowResultsInfoPopup',
  NOT_SHOW_INFO_FILTERS: 'doNotShowReportsInfoPopup',
  NOT_SHOW_INFO_DASHBOARD: 'doNotShowDashboardInfoPopup',
  NOT_SHOW_INFO_STUDIES: 'doNotShowStudiesInfoPopup',
  NOT_SHOW_INFO_STUDY_FORM: 'doNotShowStudyFormInfoPopup',
  NOT_SHOW_INFO_EXCHANGE_DASHBOARD: 'doNotShowExchangeDashboardInfoPopup',
  NOT_SHOW_INFO_JOB_PROFILE: 'doNotShowJobProfileInfoPopup',
};

export const POPUP_VISIBILITY_KEYS = {
  RESULTS: 'RESULTS',
  FILTERS: 'FILTERS',
  DASHBOARD: 'DASHBOARD',
  STUDIES: 'STUDIES',
  STUDY_FORM: 'STUDY_FORM',
  EXCHANGE_DASHBOARD: 'EXCHANGE_DASHBOARD',
  JOB_PROFILE: 'JOB_PROFILE',
  PRE_PAYWALL_PRIVACY: 'PRE_PAYWALL_PRIVACY',
};

export const QUESTION_TYPES = {
  SLIDER: 'SLIDER',
  YES_NO: 'YES_NO',
  SELECT_ONE: 'SELECT_ONE',
  SELECT_ANY: 'SELECT_ANY',
  FIND_REGION: 'FIND_REGION',
  FIND_STATE: 'FIND_STATE',
  FIND_SCHOOL: 'FIND_SCHOOL',
  RATING: 'RATING',
  CONFIRMATION: 'CONFIRMATION',
  MULTI_VARIABLE_SELECT_ONE: 'MULTI_VARIABLE_SELECT_ONE',
  GEOGRAPHY_ICO_WRAPPER: 'GEOGRAPHY_ICO_WRAPPER',
};

export const CHART_TYPES = {
  LOLLIPOP: 'lollipop',
  BAR: 'bar',
  HISTOGRAM: 'histogram',
  TREEMAP: 'treemap',
  PIE: 'pie',
};

export const QUESTION_DEFAULT_CHART_TYPE = {
  [QUESTION_TYPES.SLIDER]: CHART_TYPES.HISTOGRAM,
  [QUESTION_TYPES.YES_NO]: CHART_TYPES.BAR,
  [QUESTION_TYPES.SELECT_ONE]: CHART_TYPES.BAR,
  [QUESTION_TYPES.SELECT_ANY]: CHART_TYPES.BAR,
  [QUESTION_TYPES.FIND_REGION]: CHART_TYPES.BAR,
  [QUESTION_TYPES.FIND_STATE]: CHART_TYPES.BAR,
  [QUESTION_TYPES.FIND_SCHOOL]: null,
  [QUESTION_TYPES.RATING]: CHART_TYPES.BAR,
  [QUESTION_TYPES.CONFIRMATION]: null,
  [QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE]: CHART_TYPES.BAR,
};

export const CHART_VALUE_TYPES = {
  COUNT: 'count',
  PERCENTAGE: 'percentage',
};

export const CHART_ORIENTATIONS = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
};

export const CHART_LAYOUT_ALGORITHMS = {
  SLICE_AND_DICE: 'sliceAndDice',
  STRIPES: 'stripes',
  SQUARIFIED: 'squarified',
  STRIP: 'strip',
};

export const JOB_QUESTION_TYPES = {
  SLIDER: 'SLIDER',
  YES_NO: 'YES_NO',
  SELECT_ONE: 'SELECT_ONE',
  SELECT_ANY: 'SELECT_ANY',
  RATING: 'RATING',
  GEOGRAPHY: 'GEOGRAPHY',
  MULTI_VARIABLE_SELECT_ONE: 'MULTI_VARIABLE_SELECT_ONE',
  LOCATION: 'LOCATION',
  SPECIALTY: 'SPECIALTY',
  JOB_INFORMATION: 'JOB_INFORMATION',
  BOARD_CERTIFICATION: 'BOARD_CERTIFICATION',
  VISA_STATUS: 'VISA_STATUS',
  ETHNICITY: 'ETHNICITY',
  GENDER: 'GENDER'
};

export const JOB_OFFERS_MATCHING_STATUS = {
  PERFECT: 'perfect_matched',
  CORE: 'core_matched',
  NOT: 'not_matched',
};

export const JOB_OFFERS_MATCHING_STATUS_TITLES = {
  [JOB_OFFERS_MATCHING_STATUS.PERFECT]: 'Perfect Match',
  [JOB_OFFERS_MATCHING_STATUS.CORE]: 'Core Match',
  matched: 'Core Match',
};

export const JOB_QUESTION_TALENT_TYPE_DEPENDENCY = {
  [JOB_QUESTION_TYPES.SLIDER]: [
    { value: JOB_QUESTION_TYPES.SLIDER, text: 'Slider' },
  ],
  [JOB_QUESTION_TYPES.SELECT_ANY]: [
    { value: JOB_QUESTION_TYPES.SELECT_ANY, text: 'Select Any (Checkboxes)' },
    { value: JOB_QUESTION_TYPES.SELECT_ONE, text: 'Select One (Radios)' },
  ],
  [JOB_QUESTION_TYPES.YES_NO]: [JOB_QUESTION_TYPES.YES_NO],
  [JOB_QUESTION_TYPES.SELECT_ONE]: [
    { value: JOB_QUESTION_TYPES.SELECT_ANY, text: 'Select Any (Checkboxes)' },
    { value: JOB_QUESTION_TYPES.SELECT_ONE, text: 'Select One (Radios)' },
  ],
  [JOB_QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE]: [
    {
      value: QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE,
      text: 'Multi-Variable Select One',
    },
  ],
  [JOB_QUESTION_TYPES.RATING]: [
    { value: JOB_QUESTION_TYPES.RATING, text: 'Select Rating (1-5 Stars)' },
  ],
};

export const JOB_QUESTION_DEFAULT_CHART_TYPE = {
  [JOB_QUESTION_TYPES.SLIDER]: CHART_TYPES.HISTOGRAM,
  [JOB_QUESTION_TYPES.YES_NO]: CHART_TYPES.BAR,
  [JOB_QUESTION_TYPES.SELECT_ONE]: CHART_TYPES.BAR,
  [JOB_QUESTION_TYPES.SELECT_ANY]: CHART_TYPES.BAR,
  [JOB_QUESTION_TYPES.RATING]: CHART_TYPES.BAR,
  [JOB_QUESTION_TYPES.GEOGRAPHY]: null,
  [JOB_QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE]: CHART_TYPES.BAR,
};

export const VISA_STATUSES_TYPES = {
  US_CITIZEN: 'US',
  J1: 'J1',
  H1B: 'H1B',
  TN: 'TN',
  PERMANENT_RESIDENT: 'PERM',
  OUTSTANDING_ABILITY: 'ABILITY',
};

export const VISA_STATUSES_LABELS = {
  [VISA_STATUSES_TYPES.US_CITIZEN]: 'US Citizen',
  [VISA_STATUSES_TYPES.J1]: 'J-1 Exchange',
  [VISA_STATUSES_TYPES.H1B]: 'H-1B Temporary Specialty',
  [VISA_STATUSES_TYPES.TN]: 'TN Treaty Worker',
  [VISA_STATUSES_TYPES.PERMANENT_RESIDENT]: 'Lawful Permanent Resident',
  [VISA_STATUSES_TYPES.OUTSTANDING_ABILITY]: 'Outstanding Ability',
};

export const ETHNICITY_TYPES = {
  NATIVE: 'NATIVE',
  ASIAN: 'ASIAN',
  AFRICAN: 'AFRICAN',
  MEXICAN: 'MEXICAN',
  PUERTORICAN: 'PUERTORICAN',
  CUBAN: 'CUBAN',
  HISPANIC: 'HISPANIC',
  MIDDLEEASTERN: 'MIDDLEEASTERN',
  PACIFIC: 'PACIFIC',
  WHITE: 'WHITE',
  NA: 'NA',
  OTHER: 'OTHER'
};

export const ETHNICITY_LABELS = {
  [ETHNICITY_TYPES.NATIVE]: 'American Indian or Alaska Native',
  [ETHNICITY_TYPES.ASIAN]: 'Asian',
  [ETHNICITY_TYPES.AFRICAN]: 'Black or African American',
  [ETHNICITY_TYPES.MEXICAN]:
    'Hispanic, Latino/Latina, Spanish – Mexican, Mexican Am, or Chicano',
  [ETHNICITY_TYPES.PUERTORICAN]:
    'Hispanic, Latino/Latina, Spanish – Puerto Rican',
  [ETHNICITY_TYPES.CUBAN]: 'Hispanic, Latino/Latina, Spanish – Cuban',
  [ETHNICITY_TYPES.HISPANIC]: 'Hispanic, Latino/Latina, Spanish – Other',
  [ETHNICITY_TYPES.MIDDLEEASTERN]: 'Middle Eastern or North African',
  [ETHNICITY_TYPES.PACIFIC]: 'Native Hawaiian or other Pacific Islander',
  [ETHNICITY_TYPES.WHITE]: 'White',
  [ETHNICITY_TYPES.OTHER]: 'Other',
  [ETHNICITY_TYPES.NA]: 'I prefer not to answer',
};

export const GENDER_TYPES = {
  F: 'F',
  M: 'M',
  GV: 'GV',
  TF: 'TF',
  TM: 'TM',
  NL: 'NL',
  PNA: 'PNA',
};

export const GENDER_LABELS = {
  [GENDER_TYPES.F]: 'Female',
  [GENDER_TYPES.M]: 'Male',
  [GENDER_TYPES.GV]: 'Gender Variant/Non-Conforming',
  [GENDER_TYPES.TF]: 'Transgender/Female',
  [GENDER_TYPES.TM]: 'Transgender/Male',
  //[GENDER_TYPES.NL]: 'Not Listed',
  [GENDER_TYPES.PNA]: 'I prefer not to answer',
};

export const SETTING_TYPES = {
  STRING: 'string',
  INTEGER: 'integer',
  BOOLEAN: 'boolean',
  EMAIL: 'email',
  URL: 'url',
  JSON: 'json',
};

const popupInitialVisibilityData = {
  [POPUP_VISIBILITY_KEYS.RESULTS]: true,
  [POPUP_VISIBILITY_KEYS.FILTERS]: true,
  [POPUP_VISIBILITY_KEYS.DASHBOARD]: true,
  [POPUP_VISIBILITY_KEYS.STUDIES]: true,
  [POPUP_VISIBILITY_KEYS.EXCHANGE_DASHBOARD]: true,
  [POPUP_VISIBILITY_KEYS.JOB_PROFILE]: true,
  [POPUP_VISIBILITY_KEYS.STUDY_FORM]: false,
  [POPUP_VISIBILITY_KEYS.PRE_PAYWALL_PRIVACY]: false,
};

export const CONDITION_VARIANTS = {
  VISIBILITY: 'VISIBILITY',
  RESPONSE_VALIDATION: 'RESPONSE_VALIDATION',
};

export const COMPARATOR_TYPES = {
  LOWER: 'COMPARATOR_TYPE_LOWER',
  GRATER: 'COMPARATOR_TYPE_GRATER',
  EQUAL: 'COMPARATOR_TYPE_EQUAL',
  BETWEEN_INCLUSIVE: 'COMPARATOR_TYPE_BETWEEN_INCLUSIVE',
  BETWEEN_EXCLUSIVE: 'COMPARATOR_TYPE_BETWEEN_EXCLUSIVE',
  LOWER_OR_EQUAL: 'COMPARATOR_LOWER_OR_EQUAL',
  GRATER_OR_EQUAL: 'COMPARATOR_GRATER_OR_EQUAL',
};

export const COMPARATOR_TYPES_LABELS = {
  [COMPARATOR_TYPES.LOWER]: '<',
  [COMPARATOR_TYPES.GRATER]: '>',
  [COMPARATOR_TYPES.EQUAL]: '=',
  [COMPARATOR_TYPES.LOWER_OR_EQUAL]: '<=',
  [COMPARATOR_TYPES.GRATER_OR_EQUAL]: '>=',
  [COMPARATOR_TYPES.BETWEEN_INCLUSIVE]: 'BETWEEN (inclusive)',
  [COMPARATOR_TYPES.BETWEEN_EXCLUSIVE]: 'BETWEEN (exclusive)',
};

export const VISIBILITY_COMPARATORS = [
  COMPARATOR_TYPES.LOWER,
  COMPARATOR_TYPES.GRATER,
  COMPARATOR_TYPES.EQUAL,
  COMPARATOR_TYPES.BETWEEN_INCLUSIVE,
  COMPARATOR_TYPES.BETWEEN_EXCLUSIVE,
];

export const RESPONSE_VALUES_COMPARATORS = [
  COMPARATOR_TYPES.LOWER,
  COMPARATOR_TYPES.GRATER,
  COMPARATOR_TYPES.EQUAL,
  COMPARATOR_TYPES.LOWER_OR_EQUAL,
  COMPARATOR_TYPES.GRATER_OR_EQUAL,
];

export const MATCHING_COMPARATORS = [
  COMPARATOR_TYPES.LOWER,
  COMPARATOR_TYPES.GRATER,
  COMPARATOR_TYPES.EQUAL,
  COMPARATOR_TYPES.LOWER_OR_EQUAL,
  COMPARATOR_TYPES.GRATER_OR_EQUAL,
];

export const REPORTS_SCOPE_TYPES = {
  STUDY: 1,
  SPECIALTY: 2,
  SUB_SPECIALTY: 3,
};

export const JOB_OFFER_STATUSES = {
  ACTIVE: 'active',
  CLOSED: 'closed',
  DRAFT: 'draft',
  FILLED: 'filled',
};

export const JOB_OFFER_STATUS_NAMES = {
  active: 'Active',
  closed: 'Closed',
  draft: 'Draft',
  filled: 'Filled',
  //active: 'Active or Pending',
};

export const JOB_OFFERS_STATUSES_OPTIONS = [
  { value: JOB_OFFER_STATUSES.ACTIVE, text: 'Active' },
  { value: JOB_OFFER_STATUSES.CLOSED, text: 'Closed' },
  { value: JOB_OFFER_STATUSES.DRAFT, text: 'Draft' },
];

export const DYNAMIC_CONTENT_EMAIL_TYPES = {
  REG_EMAIL_1: 'REG_EMAIL_1',
  REG_EMAIL_2: 'REG_EMAIL_2',
  REG_EMAIL_ACCOUNT_CREATED: 'REG_EMAIL_ACCOUNT_CREATED',
  STUDY_COMPLETED_EMAIL: 'STUDY_COMPLETED_EMAIL',
  SEND_TOKEN_EMAIL: 'SEND_TOKEN_EMAIL',
  RECRUITER_EMAIL_VERIFICATION: 'RECRUITER_EMAIL_VERIFICATION',
};
export const DYNAMIC_CONTENT_EMAIL_TYPE_OPTIONS = [
  { value: DYNAMIC_CONTENT_EMAIL_TYPES.REG_EMAIL_1, text: 'Registration 1' },
  { value: DYNAMIC_CONTENT_EMAIL_TYPES.REG_EMAIL_2, text: 'Registration 2' },
  {
    value: DYNAMIC_CONTENT_EMAIL_TYPES.REG_EMAIL_ACCOUNT_CREATED,
    text: 'Account Created',
  },
  {
    value: DYNAMIC_CONTENT_EMAIL_TYPES.STUDY_COMPLETED_EMAIL,
    text: 'Study Completed Email',
  },
  {
    value: DYNAMIC_CONTENT_EMAIL_TYPES.SEND_TOKEN_EMAIL,
    text: 'Send Token Email',
  },
  {
    value: DYNAMIC_CONTENT_EMAIL_TYPES.RECRUITER_EMAIL_VERIFICATION,
    text: 'Recruiter Email Verification',
  },
];

export const EVENT_TYPES = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  ENTER_PAGE: 'ENTER_PAGE',
  VIEW_JOB_BOARD_URL: 'VIEW_JOB_BOARD_URL',
  REVEAL_JOB_BOARD_URL: 'REVEAL_JOB_BOARD_URL',
};

export const EVENT_PARAMETERS = {
  ENTER_PAGE_FROM: 'ENTER_PAGE_FROM',
  ENTER_PAGE_NAME: 'ENTER_PAGE_NAME',
  ENTER_PAGE_TO: 'ENTER_PAGE_TO',
  VIEW_JOB_BOARD_URL_EVENT_JOB_OFFER: 'VIEW_JOB_BOARD_URL_EVENT_JOB_OFFER',
  VIEW_JOB_BOARD_URL_EVENT_URL: 'VIEW_JOB_BOARD_URL_EVENT_URL',
  REVEAL_JOB_BOARD_URL_EVENT_URL: 'REVEAL_JOB_BOARD_URL_EVENT_URL',
};

export const GUEST_USER_ROLES = {
  ROLE_RECRUITER: 'recruiter',
  ROLE_PHYSICIAN: 'physician',
};

export const COOKIES = {
  GUEST_CURRENT_ROLE: 'guestCurrentRole',
  GUEST_ICO_BROWSER_CRITERIA_ITEM_VALUES:
    'GUEST_ICO_BROWSER_CRITERIA_ITEM_VALUES',

  RECRUITER: {
    INFORM: {
      SPECIALTY_ID: 'RECRUITER_INFORM_SPECIALTY_ID',
      SUBSPECIALTY_ID: 'RECRUITER_INFORM_SUBSPECIALTY_ID',
      QUESTIONNAIRE_ID: 'RECRUITER_INFORM_QUESTIONNAIRE_ID',
    },
  },
};

export const store = new Vuex.Store({
  state: {
    token: null,
    user: null, // used for everything else, including profile
    guestCurrentRole: GUEST_USER_ROLES.ROLE_PHYSICIAN,
    userSettings: {},
    primaryQuestionnaireComparisons: null,
    sideBarExpanded: true,
    mobileNavExpanded: false,
    societyOptions: [], // formatted for use in a <b-form-select>
    specialtyOptions: [], // formatted for use in a <b-form-select>
    subspecialtyOptions: [], // formatted for use in a <b-form-select>
    userQuestionnaireLoading: false,
    ethnicityOptions: [
      { value: ETHNICITY_TYPES.NATIVE, text: ETHNICITY_LABELS.NATIVE },
      { value: ETHNICITY_TYPES.ASIAN, text: ETHNICITY_LABELS.ASIAN },
      { value: ETHNICITY_TYPES.AFRICAN, text: ETHNICITY_LABELS.AFRICAN },
      {
        value: ETHNICITY_TYPES.MEXICAN,
        text: ETHNICITY_LABELS.MEXICAN,
      },
      {
        value: ETHNICITY_TYPES.PUERTORICAN,
        text: ETHNICITY_LABELS.PUERTORICAN,
      },
      { value: ETHNICITY_TYPES.CUBAN, text: ETHNICITY_LABELS.CUBAN },
      { value: ETHNICITY_TYPES.HISPANIC, text: ETHNICITY_LABELS.HISPANIC },
      {
        value: ETHNICITY_TYPES.MIDDLEEASTERN,
        text: ETHNICITY_LABELS.MIDDLEEASTERN,
      },
      { value: ETHNICITY_TYPES.PACIFIC, text: ETHNICITY_LABELS.PACIFIC },
      { value: ETHNICITY_TYPES.WHITE, text: ETHNICITY_LABELS.WHITE },
      { value: ETHNICITY_TYPES.OTHER, text: ETHNICITY_LABELS.OTHER },
      { value: ETHNICITY_TYPES.NA, text: ETHNICITY_LABELS.NA },
    ],
    genderOptions: [
      { value: null, text: 'Select gender identity...' },
      { value: GENDER_TYPES.F, text: GENDER_LABELS.F },
      { value: GENDER_TYPES.M, text: GENDER_LABELS.M },
      { value: GENDER_TYPES.GV, text: GENDER_LABELS.GV },
      { value: GENDER_TYPES.TF, text: GENDER_LABELS.TF },
      { value: GENDER_TYPES.TM, text: GENDER_LABELS.TM },
        // { value: GENDER_TYPES.NL, text: GENDER_LABELS.NL },
        { value: GENDER_TYPES.PNA, text: GENDER_LABELS.PNA },
    ],
    questionTypeOptions: [
      { value: QUESTION_TYPES.SLIDER, text: 'Slider' },
      { value: QUESTION_TYPES.YES_NO, text: 'Yes/No' },
      { value: QUESTION_TYPES.SELECT_ONE, text: 'Select One (Radios)' },
      {
        value: QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE,
        text: 'Multi-Variable Select One',
      },
      { value: QUESTION_TYPES.SELECT_ANY, text: 'Select Any (Checkboxes)' },
      { value: QUESTION_TYPES.FIND_REGION, text: 'Select Region' },
      { value: QUESTION_TYPES.FIND_STATE, text: 'Select State' },
      { value: QUESTION_TYPES.FIND_SCHOOL, text: 'Select Medical School' },
      { value: QUESTION_TYPES.RATING, text: 'Select Rating (1-3 Stars)' },
      { value: QUESTION_TYPES.CONFIRMATION, text: 'Confirmation' },
    ],
    jobQuestionTypeOptions: [
      { value: JOB_QUESTION_TYPES.SLIDER, text: 'Slider' },
      { value: JOB_QUESTION_TYPES.YES_NO, text: 'Yes/No' },
      { value: JOB_QUESTION_TYPES.SELECT_ONE, text: 'Select One (Radios)' },
      {
        value: QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE,
        text: 'Multi-Variable Select One',
      },
      { value: JOB_QUESTION_TYPES.SELECT_ANY, text: 'Select Any (Checkboxes)' },
      { value: JOB_QUESTION_TYPES.RATING, text: 'Select Rating (1-5 Stars)' },
    ],
    chartTypeOptions: [
      {
        value: CHART_TYPES.LOLLIPOP,
        text: 'Lollipop',
        questionTypes: [
          QUESTION_TYPES.YES_NO,
          QUESTION_TYPES.SELECT_ONE,
          QUESTION_TYPES.SELECT_ANY,
          QUESTION_TYPES.FIND_STATE,
          QUESTION_TYPES.FIND_REGION,
          QUESTION_TYPES.RATING,
          QUESTION_TYPES.SLIDER,
          QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE,
        ],
      },
      {
        value: CHART_TYPES.BAR,
        text: 'Bar',
        questionTypes: [
          QUESTION_TYPES.YES_NO,
          QUESTION_TYPES.SELECT_ONE,
          QUESTION_TYPES.SELECT_ANY,
          QUESTION_TYPES.FIND_REGION,
          QUESTION_TYPES.FIND_STATE,
          QUESTION_TYPES.RATING,
          QUESTION_TYPES.SLIDER,
          QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE,
        ],
      },
      {
        value: CHART_TYPES.HISTOGRAM,
        text: 'Histogram',
        questionTypes: [QUESTION_TYPES.SLIDER],
      },
      {
        value: CHART_TYPES.TREEMAP,
        text: 'TreeMap',
        questionTypes: [
          QUESTION_TYPES.SELECT_ONE,
          QUESTION_TYPES.SELECT_ANY,
          QUESTION_TYPES.FIND_REGION,
          QUESTION_TYPES.FIND_STATE,
          QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE,
        ],
      },
      {
        value: CHART_TYPES.PIE,
        text: 'Pie',
        questionTypes: [
          QUESTION_TYPES.YES_NO,
          QUESTION_TYPES.SELECT_ONE,
          QUESTION_TYPES.SELECT_ANY,
          QUESTION_TYPES.FIND_REGION,
          QUESTION_TYPES.FIND_STATE,
          QUESTION_TYPES.RATING,
          QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE,
        ],
      },
    ],
    chartValueTypeOptions: [
      { value: CHART_VALUE_TYPES.COUNT, text: 'Counts' },
      { value: CHART_VALUE_TYPES.PERCENTAGE, text: 'Percentage' },
    ],
    chartOrientationOptions: [
      { value: CHART_ORIENTATIONS.VERTICAL, text: 'Vertical' },
      { value: CHART_ORIENTATIONS.HORIZONTAL, text: 'Horizontal' },
    ],
    chartLayoutAlgorithmOptions: [
      { value: CHART_LAYOUT_ALGORITHMS.SLICE_AND_DICE, text: 'Slice and Dice' },
      { value: CHART_LAYOUT_ALGORITHMS.STRIPES, text: 'Stripes' },
      { value: CHART_LAYOUT_ALGORITHMS.SQUARIFIED, text: 'Squarified' },
      { value: CHART_LAYOUT_ALGORITHMS.STRIP, text: 'Strip' },
    ],
    roleOptions: [
      { value: ROLE_GM_ADMIN, text: 'PHAIRIFY_ADMIN' },
      { value: ROLE_MSS_ADMIN, text: 'MSS_ADMIN' },
      { value: ROLE_MSS_USER, text: 'MSS_USER' },
      { value: ROLE_MSS_RECRUITER, text: 'MSS_RECRUITER' },
      { value: ROLE_NON_MSS_USER, text: 'NON_MSS_USER' },
    ],
    settings: {},
    society: null,
    popupInitialVisibilityData: popupInitialVisibilityData,
    surveysDataMapper: {},
    surveysLoading: false,
    isPasswordResetRequired: false,
    presentationalQuestionnaire: null,
    isProduction: process.env.NODE_ENV === 'production',
  },
  getters: {
    isAuthenticated: (state) => Boolean(state.token),
    userIsAdmin: () => {
      return store.getters.userIsGMAdmin || store.getters.userIsMSSAdmin;
    },
    userIsGMAdmin: (state) =>
      state.user &&
      state.user.roles &&
      state.user.roles.includes(ROLE_GM_ADMIN),
    userIsMSSAdmin: (state) =>
      state.user &&
      state.user.roles &&
      state.user.roles.includes(ROLE_MSS_ADMIN),
    userIsMSSRecruiter: (state) =>
      state.user &&
      state.user.roles &&
      state.user.roles.includes(ROLE_MSS_RECRUITER),
    userIsMSSUser: (state) =>
      state.user &&
      state.user.roles &&
      state.user.roles.includes(ROLE_MSS_USER),
    userIsNonMSSUser: (state) =>
      state.user &&
      state.user.roles &&
      state.user.roles.includes(ROLE_NON_MSS_USER),
    userHasInvalidEmail: (state) =>
      state.user && state.user.is_email_validated === 0,
    questionnaire: (state) => {
      return state.user ? state.user.questionnaire : null;
    },
    isGuestRecruiter: (state) => {
      return state.guestCurrentRole === GUEST_USER_ROLES.ROLE_RECRUITER;
    },
    completedStudies: (state) => {
      return state.user ? state.user.completed_surveys : [];
    },
    hasCompletedPrimaryStudy: (state) => {
      const questionnaire = state.user ? state.user.questionnaire : null;
      if (!questionnaire) return null;
      const completedStudies = state.user ? state.user.completed_surveys : [];

      return completedStudies.some(
        (s) => s.questionnaire_id === questionnaire.id
      );
    },
    society: (state) => state.society,
    getUserSetting: (state) => (settingName) =>
      state.userSettings[settingName] || null,
    getSetting: (state) => (settingName) => state.settings[settingName] || null,
    getInfoPopupVisibility: (state) => (popupType) =>
      !!state.popupInitialVisibilityData[popupType],
    getSurveyData: (state) => (surveyId) =>
      state.surveysDataMapper[surveyId] || null,
    userDefaultJobProfile: (state) =>
      state.user ? state.user.defaultJobProfile : null,
    isDemoMode: (state) => {
      return Boolean(parseInt(state.settings.enable_demo_functionality));
    },
    presentationalQuestionnaire: (state) => state.presentationalQuestionnaire,
    societySpecialtyOptions: (state) =>
      state.specialtyOptions.filter(
        ({ specialty }) => parseInt(specialty.is_master) === 0
      ),
    masterSpecialtyOptions: (state) =>
      state.specialtyOptions.filter(
        ({ specialty }) => parseInt(specialty.is_master) === 1
      ),
    masterSubspecialtyOptions: (state) =>
      state.subspecialtyOptions.filter(
        ({ subspecialty }) => parseInt(subspecialty.is_master) === 1
      ),
    masterSubspecialtyOptionsBySpecialty: (state, getters) => (specialtyId) => {
      const { specialty = null } =
        getters.societySpecialtyOptions.find(
          ({ value }) => value === specialtyId
        ) || {};
      if (!specialty) return [];
      const masterSpecialty = getters.masterSpecialtyOptions.find(
        ({ specialty: { code } }) => code === specialty.master_code
      );
      if (!masterSpecialty) return [];
      return getters.masterSubspecialtyOptions.filter(
        ({ subspecialty: { specialty_id } }) =>
          specialty_id === masterSpecialty.value
      );
    },
    masterSubspecialtyOptionsByMasterSpecialty:
      (state, getters) => (specialtyId) => {
        return getters.masterSubspecialtyOptions.filter(
          ({ subspecialty: { specialty_id } }) => specialty_id === specialtyId
        );
      },
    isProduction: (state) => state.isProduction,
  },
  mutations: {
    [SET_USER_MUTATION]: function (state, user) {
      state.user = user;
      if (user && user.settings && user.settings !== '') {
        state.userSettings = JSON.parse(user.settings);
      }
      if (
        user &&
        user.primaryQuestionnaireComparisons &&
        user.primaryQuestionnaireComparisons.comparisons
      ) {
        state.primaryQuestionnaireComparisons = JSON.parse(
          user.primaryQuestionnaireComparisons.comparisons
        );
      }
    },
    [SET_USER_PROFILE_MUTATION]: function (state, payload) {
      state.user = { ...state.user, ...payload };
    },
    [SET_TOKEN_MUTATION]: function (state, value) {
      state.token = value;
    },
    [SET_GUEST_USER_ROLE_MUTATION]: function (state, value) {
      state.guestCurrentRole = value;
      Vue.$cookies.set(COOKIES.GUEST_CURRENT_ROLE, value);
    },
    [ADD_SOCIETY_MUTATION]: function (state, newSociety) {
      Vue.set(state.societyOptions, state.societyOptions.length, {
        value: newSociety.id,
        text: newSociety.name,
        society: newSociety,
      });
    },
    [SET_USER_QUESTIONNAIRE_MUTATION]: function (state, questionnaire) {
      if (state.user) {
        Vue.set(state.user, 'questionnaire', questionnaire);
      }
    },
    [SET_USER_SURVEY_MUTATION]: function (state, survey) {
      if (state.user && state.user.questionnaire) {
        Vue.set(state.user.questionnaire, 'survey', survey);
      }
    },
    [SET_PRIMARY_QUESTIONNAIRE_COMPARISONS_MUTATION]: function (
      state,
      comparisons
    ) {
      state.primaryQuestionnaireComparisons = comparisons;
    },
    [UPDATE_SOCIETY_DATA_MUTATION]: function (state, updatedSociety) {
      state.societyOptions.forEach((societyOption, i) => {
        if (
          societyOption.society &&
          societyOption.society.id === updatedSociety.id
        ) {
          Vue.set(state.societyOptions, i, {
            ...societyOption,
            society: updatedSociety,
          });
        }
      });
    },
    [SET_SETTINGS_MUTATION]: function (state, settings) {
      state.settings = settings;
    },
    [UPDATE_SETTING_MUTATION]: function (state, setting) {
      Vue.set(state.settings, setting.key, setting.value);
    },
    [SET_SOCIETY_MUTATION]: function (state, society) {
      state.society = society;
    },
    [UPDATE_SOCIETY_MUTATION]: function (state, society) {
      Vue.set(state, 'society', society);
    },
    [SET_USER_SETTINGS_MUTATION]: function (state, settings) {
      state.userSettings = settings;
    },
    [UPDATE_USER_SETTING_MUTATION]: function (state, setting) {
      Vue.set(state.userSettings, setting.key, setting.value);
    },
    [SET_POPUP_VISIBILITY_MUTATION]: function (state, payload) {
      Vue.set(state.popupInitialVisibilityData, payload.key, payload.value);
    },
    [ADD_SURVEY_DATA_MUTATION]: function (state, payload) {
      Vue.set(state.surveysDataMapper, payload.key, payload.data);
    },
    [ADD_SPECIALTY_MUTATION]: function (state, payload) {
      state.specialtyOptions.push({
        value: payload.id,
        text: payload.title,
        specialty: payload,
      });
    },
    [UPDATE_SPECIALTY_MUTATION]: function (state, payload) {
      const currIndex = state.specialtyOptions.findIndex(
        (s) => s.value === payload.id
      );
      if (currIndex > -1) {
        Vue.set(state.specialtyOptions, currIndex, {
          value: payload.id,
          text: payload.title,
          specialty: payload,
        });
      }
    },
    [DELETE_SPECIALTY_MUTATION]: function (state, payload) {
      const index = state.specialtyOptions.findIndex(
        ({ value }) => value === payload
      );
      if (index === -1) return;
      state.specialtyOptions.splice(index, 1);
    },
    [ADD_SUBSPECIALTY_MUTATION]: function (state, payload) {
      state.subspecialtyOptions.push({
        value: payload.id,
        text: payload.title,
        subspecialty: payload,
      });
    },
    [UPDATE_SUBSPECIALTY_MUTATION]: function (state, payload) {
      const currIndex = state.subspecialtyOptions.findIndex(
        (s) => s.value === payload.id
      );
      if (currIndex > -1) {
        Vue.set(state.subspecialtyOptions, currIndex, payload);
      }
    },
    [DELETE_SUBSPECIALTY_MUTATION]: function (state, payload) {
      const index = state.subspecialtyOptions.findIndex(
        ({ value }) => value === payload
      );
      if (index === -1) return;
      state.subspecialtyOptions.splice(index, 1);
    },
    [SET_USER_DEFAULT_JOB_PROFILE_MUTATION]: function (state, payload) {
      if (!state.user) return;
      state.user.defaultJobProfile = payload;
    },
    [SET_SURVEYS_LOADING_MUTATION]: function (state, payload) {
      state.surveysLoading = payload;
    },
    [SET_PASSWORD_RESET_REQUIRED_MUTATION]: function (state, payload) {
      state.isPasswordResetRequired = payload;
    },
    [SET_PRESENTATIONAL_QUESTIONNAIRE_MUTATION]: function (state, payload) {
      state.presentationalQuestionnaire = payload;
    },
    [DELETE_RESET_SURVEY_FROM_COMPLETED_MUTATION]: function (state, payload) {
      if (!state.user || !Array.isArray(state.user.completed_surveys)) return;
      const index = state.user.completed_surveys.findIndex(
        ({ id }) => id === payload
      );
      if (index === -1) return;
      state.user.completed_surveys.splice(index, 1);
    },
    [SET_USER_QUESTIONNAIRE_LOADING_MUTATION]: function (state, payload) {
      state.userQuestionnaireLoading = payload;
    },
    [SET_SOCIETIES_OPTIONS_MUTATION]: function (state, value) {
      state.societyOptions = value;
    },
  },
  actions: {
    [ADD_SOCIETY_ACTION]: async function ({ commit }, s) {
      const data = await Api.createSociety(s);

      commit(ADD_SOCIETY_MUTATION, data);

      return data;
    },
    [GET_SOCIETIES_ACTION]: async function ({ commit }) {
      const societies = await Api.getAllSocieties();
      const societyOptions = [
        { value: null, text: 'Select society...', society: null },
        ...map(
          sortBy(societies, (s) => s.name.trim()),
          (s) => {
            return {
              value: s.id,
              text: s.name,
              society: s,
            };
          }
        ),
      ];
      commit(SET_SOCIETIES_OPTIONS_MUTATION, societyOptions);
    },
    [GET_SPECIALTIES_ACTION]: function () {
      return Api.getAllSpecialties().then((specialties) => {
        this.state.specialtyOptions = map(specialties, (s) => {
          return { value: s.id, text: s.title, specialty: s };
        });
      });
    },
    [GET_SUBSPECIALTIES_ACTION]: function () {
      return Api.getAllSubspecialties().then((subspecialties) => {
        this.state.subspecialtyOptions = map(subspecialties, (s) => {
          return {
            value: s.id,
            text: s.title,
            subspecialty: s,
          };
        });
      });
    },
    [AUTH_ACTION]: function ({ commit, dispatch, getters }, { user, token }) {
      saveToStorage('user-token', token);
      commit(SET_TOKEN_MUTATION, token);
      commit(SET_USER_MUTATION, user);
      if ((user || {}).defaultJobProfile && !getters.defaultJobProfile) {
        commit(SET_USER_DEFAULT_JOB_PROFILE_MUTATION, user.defaultJobProfile);
      }
      if ((user || {}).roles && user.roles.includes(ROLE_GM_ADMIN)) {
        dispatch(GET_SOCIETIES_ACTION);
      }
      logUserEvent(EVENT_TYPES.LOGIN);
      Vue.$cookies.remove(COOKIES.GUEST_CURRENT_ROLE);
    },
    [SIGN_OUT_ACTION]: async function ({ commit }) {
      try {
        logUserEvent(EVENT_TYPES.LOGOUT);
        await Api.deleteRefreshToken();
      } catch (error) {
        process.env.NODE_ENV !== 'production' && console.error(error);
      }
      saveToStorage('user-token', null);
      commit(SET_USER_MUTATION, null);
      commit(SET_TOKEN_MUTATION, null);
      commit(SET_USER_SETTINGS_MUTATION, {});
      Object.keys(popupInitialVisibilityData).forEach((key) =>
        commit(SET_POPUP_VISIBILITY_MUTATION, {
          key: key,
          value: popupInitialVisibilityData[key],
        })
      );
      commit(CLEAR_JOB_PROFILES_MUTATION, null);
    },
    [GET_CURRENT_USER_ACTION]: async function ({ commit, getters }, vue) {
      try {
        const user = await Api.getCurrentUser(vue);
        commit(SET_USER_MUTATION, user);
        if ((user || {}).defaultJobProfile && !getters.defaultJobProfile) {
          commit(SET_USER_DEFAULT_JOB_PROFILE_MUTATION, user.defaultJobProfile);
        }
      } catch (error) {
        console.error(error);
        vue.$toasted.show('Some error occurs', {
          type: 'error',
          duration: 3000,
        });
        commit(SET_USER_MUTATION, null);
      }
    },
    [GET_SETTINGS_ACTION]: function ({ commit }) {
      return Api.getSettings().then((settings) => {
        commit(SET_SETTINGS_MUTATION, settings);
      });
    },
    [GET_SOCIETY_ACTION]: function ({ commit }) {
      return Api.mssAdminGetSociety().then((society) => {
        commit(SET_SOCIETY_MUTATION, society);
      });
    },
    [CREATE_SURVEY_ACTION]: async function (
      { commit, getters },
      { questionnaireId }
    ) {
      const { survey, defaultJobProfile } = await Api.createSurvey(
        questionnaireId
      );
      commit(SET_USER_SURVEY_MUTATION, survey);
      if (!getters.userDefaultJobProfile) {
        commit(SET_USER_DEFAULT_JOB_PROFILE_MUTATION, defaultJobProfile);
      }
      return survey;
    },
    [GET_SURVEY_ACTION]: async function (
      { commit, getters },
      { surveyId, sectionId = null }
    ) {
      commit(SET_SURVEYS_LOADING_MUTATION, true);
      try {
        const { survey, defaultJobProfile = null } = await Api.getSurvey(
          surveyId,
          sectionId
        );
        if (
          defaultJobProfile &&
          (!getters.userDefaultJobProfile ||
            getters.userDefaultJobProfile.id !== defaultJobProfile.id)
        ) {
          commit(SET_USER_DEFAULT_JOB_PROFILE_MUTATION, defaultJobProfile);
        }

        const { is_support_job_preference, period } = survey.questionnaire;
        let questionsCount = 0;
        survey.questionnaire.sections = survey.questionnaire.sections.filter(
          ({ id, is_module }) =>
            sectionId ? parseInt(sectionId) === parseInt(id) : !is_module
        );
        let newResponses = [];
        (survey.questionnaire || {}).sections.forEach((section) => {
          section.questionSections.forEach((qs) => {
            const question = qs.question;
            if (question) questionsCount += 1;
            if (question && Array.isArray(question.variables)) {
              question.variables.forEach((v) => {
                const savedResponse = survey.responses.find((r) => {
                  return r.variable_id === v.id;
                });
                newResponses.push(savedResponse);
                // savedResponse should always be true; responses are created when survey
                // ... UNLESS a question was added since the survey was created
                // (hopefully not in real life)
                v.response = savedResponse || {
                  survey_id: survey.id,
                  variable_id: v.id,
                  is_unknown: 0,
                };
                if (
                  (question.type === QUESTION_TYPES.SELECT_ONE ||
                    question.type === QUESTION_TYPES.SELECT_ANY ||
                    question.type === QUESTION_TYPES.YES_NO) &&
                  v.response.value === 1
                ) {
                  v.response.value = '1';
                }
                if (
                  question.type === QUESTION_TYPES.YES_NO &&
                  v.response.value === 0
                ) {
                  v.response.value = '0';
                }
              });
              if (question.randomize_variables) {
                question.variables = Utils.shuffleArray(question.variables);
              }
            }
            if (
              question &&
              question.relatedJobQuestion &&
              Array.isArray(question.relatedJobQuestion.jobQuestionVariables)
            ) {
              question.relatedJobQuestion.jobQuestionVariables.forEach(
                (jQv) => {
                  if (
                    !Array.isArray(jQv.defaultJobProfileQuestionResponses) ||
                    jQv.defaultJobProfileQuestionResponses.length === 0
                  ) {
                    jQv.jobProfileQuestionResponses = [
                      {
                        job_profile_id: getters.userDefaultJobProfile
                          ? getters.userDefaultJobProfile.id
                          : null,
                        job_question_variable_id: jQv.id,
                        value: null,
                        value_string: null,
                        no_preference: false,
                      },
                    ];
                  } else {
                    jQv.jobProfileQuestionResponses = [
                      ...jQv.defaultJobProfileQuestionResponses,
                    ];
                    delete jQv.defaultJobProfileQuestionResponses;
                  }
                }
              );
            }
          });
        });
        survey.responses = newResponses;
        const surveyData = {
          survey,
          is_support_job_preference,
          period,
          questionsCount,
        };
        commit(ADD_SURVEY_DATA_MUTATION, {
          key: surveyId,
          data: surveyData,
        });
        commit(SET_SURVEYS_LOADING_MUTATION, false);
        return surveyData;
      } catch (e) {
        commit(SET_SURVEYS_LOADING_MUTATION, false);
        throw e;
      }
    },
    [GET_USER_QUESTIONNAIRE_ACTION]: async function ({ commit, state }) {
      commit(SET_USER_QUESTIONNAIRE_LOADING_MUTATION, true);
      try {
        const userData = await Api.getCurrentUserQuestionnaireData();
        commit(SET_USER_MUTATION, { ...state.user, ...userData });
      } finally {
        commit(SET_USER_QUESTIONNAIRE_LOADING_MUTATION, false);
      }
    },
    [SWITCH_GUEST_USER_ROLE_ACTION]: async function ({ commit }, { role }) {
      commit(SET_GUEST_USER_ROLE_MUTATION, role);
    },
  },
  modules: {
    jobProfiles,
    boardCertifications,
    certifyingBoards,
    societyCertifyingBoards,
    membershipTypes,
    memberTypeMappings,
    studyTypes,
    conditionVariables,
  },
});
